import { useEffect, useState } from 'react'
import { ImageIcon, Plus } from 'lucide-react'
import { emptyDisplayAssets, normalizeDisplayAssets, parseLineSeparatedUrls } from '@/lib/projectDisplay'

function ensureAssets(form) {
  return normalizeDisplayAssets(form.display_assets || emptyDisplayAssets())
}

export default function HubDisplayAssetsPanel({ form, setForm, projectId }) {
  const da = ensureAssets(form)
  const [mmText, setMmText] = useState(() =>
    JSON.stringify(da.mind_map || { nodes: [], edges: [] }, null, 2)
  )
  const [mmError, setMmError] = useState('')

  useEffect(() => {
    const d = ensureAssets(form)
    setMmText(JSON.stringify(d.mind_map || { nodes: [], edges: [] }, null, 2))
    setMmError('')
  }, [projectId])

  const setDa = (patch) => {
    setForm((f) => {
      const base = ensureAssets(f)
      let next = { ...base, ...patch }
      if (patch.credits) {
        next = { ...next, credits: { ...base.credits, ...patch.credits } }
      }
      return {
        ...f,
        display_assets: normalizeDisplayAssets(next),
      }
    })
  }

  const commitMindMap = () => {
    try {
      const parsed = JSON.parse(mmText)
      setMmError('')
      setDa({
        mind_map: {
          nodes: Array.isArray(parsed.nodes) ? parsed.nodes : [],
          edges: Array.isArray(parsed.edges) ? parsed.edges : [],
        },
      })
    } catch {
      setMmError('Invalid JSON')
    }
  }

  const setUrlList = (key, text) => {
    setDa({ [key]: parseLineSeparatedUrls(text) })
  }

  const addKeyStatement = () => {
    setDa({ key_statements: [...da.key_statements, { title: '', content: '' }] })
  }

  const updateKeyStatement = (i, patch) => {
    const next = [...da.key_statements]
    next[i] = { ...next[i], ...patch }
    setDa({ key_statements: next })
  }

  const removeKeyStatement = (i) => {
    setDa({ key_statements: da.key_statements.filter((_, j) => j !== i) })
  }

  const addKeyElement = () => {
    setDa({ key_elements: [...da.key_elements, { title: '', description: '' }] })
  }

  const updateKeyElement = (i, patch) => {
    const next = [...da.key_elements]
    next[i] = { ...next[i], ...patch }
    setDa({ key_elements: next })
  }

  const removeKeyElement = (i) => {
    setDa({ key_elements: da.key_elements.filter((_, j) => j !== i) })
  }

  return (
    <div className="rounded-2xl border border-border/80 bg-muted/5 p-5 space-y-6">
      <div>
        <div className="flex items-center gap-2 text-sm font-semibold tracking-tight">
          <ImageIcon className="h-4 w-4 text-muted-foreground" aria-hidden />
          Public page assets
        </div>
        <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
          HTTPS URLs only — from Supabase Storage, Cloudflare R2, or any CDN. First hero URL is used as the grid
          thumbnail when published. You do not need a separate R2 bucket unless you want assets isolated there.
        </p>
      </div>

      <div className="space-y-1">
        <label className="text-[11px] font-medium text-muted-foreground">Hero images (one URL per line)</label>
        <textarea
          className="mt-1 w-full min-h-[72px] rounded-xl border border-input bg-background px-3 py-2 font-mono text-xs"
          value={da.hero_image_urls.join('\n')}
          onChange={(e) => setUrlList('hero_image_urls', e.target.value)}
          placeholder="https://…"
        />
      </div>

      <div className="space-y-1">
        <label className="text-[11px] font-medium text-muted-foreground">Work in progress (one URL per line)</label>
        <textarea
          className="mt-1 w-full min-h-[72px] rounded-xl border border-input bg-background px-3 py-2 font-mono text-xs"
          value={da.wip_image_urls.join('\n')}
          onChange={(e) => setUrlList('wip_image_urls', e.target.value)}
          placeholder="https://…"
        />
      </div>

      <div className="space-y-1">
        <label className="text-[11px] font-medium text-muted-foreground">Moodboard (one URL per line)</label>
        <textarea
          className="mt-1 w-full min-h-[72px] rounded-xl border border-input bg-background px-3 py-2 font-mono text-xs"
          value={da.moodboard_image_urls.join('\n')}
          onChange={(e) => setUrlList('moodboard_image_urls', e.target.value)}
          placeholder="https://…"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-medium text-muted-foreground">Key statements</span>
          <button type="button" onClick={addKeyStatement} className="inline-flex items-center gap-1 text-xs text-primary">
            <Plus className="h-3 w-3" /> Add
          </button>
        </div>
        {da.key_statements.length === 0 ? (
          <p className="text-xs text-muted-foreground">None</p>
        ) : (
          <div className="space-y-2">
            {da.key_statements.map((row, i) => (
              <div key={i} className="rounded-xl border border-border/60 bg-background/80 p-3 space-y-2">
                <input
                  className="w-full rounded-lg border border-input bg-background px-2 py-1.5 text-xs"
                  placeholder="Title"
                  value={row.title}
                  onChange={(e) => updateKeyStatement(i, { title: e.target.value })}
                />
                <textarea
                  className="w-full min-h-[60px] rounded-lg border border-input bg-background px-2 py-1.5 text-xs"
                  placeholder="Content"
                  value={row.content}
                  onChange={(e) => updateKeyStatement(i, { content: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => removeKeyStatement(i)}
                  className="text-xs text-muted-foreground hover:text-destructive"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-medium text-muted-foreground">Key elements</span>
          <button type="button" onClick={addKeyElement} className="inline-flex items-center gap-1 text-xs text-primary">
            <Plus className="h-3 w-3" /> Add
          </button>
        </div>
        {da.key_elements.length === 0 ? (
          <p className="text-xs text-muted-foreground">None</p>
        ) : (
          <div className="space-y-2">
            {da.key_elements.map((row, i) => (
              <div key={i} className="rounded-xl border border-border/60 bg-background/80 p-3 space-y-2">
                <input
                  className="w-full rounded-lg border border-input bg-background px-2 py-1.5 text-xs"
                  placeholder="Title"
                  value={row.title}
                  onChange={(e) => updateKeyElement(i, { title: e.target.value })}
                />
                <textarea
                  className="w-full min-h-[60px] rounded-lg border border-input bg-background px-2 py-1.5 text-xs"
                  placeholder="Description"
                  value={row.description}
                  onChange={(e) => updateKeyElement(i, { description: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => removeKeyElement(i)}
                  className="text-xs text-muted-foreground hover:text-destructive"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-1">
        <label className="text-[11px] font-medium text-muted-foreground">
          Mind map (JSON) — blur to save. Nodes: {'[{id,label}]'}; edges: {'[{from,to,label?}]'}
        </label>
        <textarea
          className={`mt-1 w-full min-h-[120px] rounded-xl border bg-background px-3 py-2 font-mono text-[11px] ${
            mmError ? 'border-destructive' : 'border-input'
          }`}
          value={mmText}
          onChange={(e) => {
            setMmText(e.target.value)
            setMmError('')
          }}
          onBlur={commitMindMap}
        />
        {mmError ? <p className="text-xs text-destructive">{mmError}</p> : null}
      </div>

      <div className="space-y-1">
        <label className="text-[11px] font-medium text-muted-foreground">3D model URL (.glb / .gltf)</label>
        <input
          type="url"
          className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 font-mono text-xs"
          value={da.model_url}
          onChange={(e) => setDa({ model_url: e.target.value })}
          placeholder="https://…"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className="text-[10px] text-muted-foreground">Credits — client</label>
          <input
            className="mt-0.5 w-full rounded-lg border border-input bg-background px-2 py-1.5 text-xs"
            value={da.credits.client_name}
            onChange={(e) => setDa({ credits: { client_name: e.target.value } })}
          />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground">Date</label>
          <input
            type="date"
            className="mt-0.5 w-full rounded-lg border border-input bg-background px-2 py-1.5 text-xs"
            value={da.credits.project_date ? da.credits.project_date.slice(0, 10) : ''}
            onChange={(e) => setDa({ credits: { project_date: e.target.value } })}
          />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground">Participants</label>
          <input
            className="mt-0.5 w-full rounded-lg border border-input bg-background px-2 py-1.5 text-xs"
            value={da.credits.participants}
            onChange={(e) => setDa({ credits: { participants: e.target.value } })}
            placeholder="Names, comma-separated"
          />
        </div>
      </div>
    </div>
  )
}
